// Service reCAPTCHA v3 + v2 fallback pour générer les tokens côté client
import env from "@/config/env";
import { logger } from "@/lib/logger";

const RECAPTCHA_V3_SITE_KEY = env.RECAPTCHA_V3_SITE_KEY;
const RECAPTCHA_V2_SITE_KEY = env.RECAPTCHA_V2_SITE_KEY;

// Types pour reCAPTCHA
interface GrecaptchaWindow extends Window {
  grecaptcha?: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
    render: (container: string | HTMLElement, options: Record<string, unknown>) => number;
    getResponse: (widgetId?: number) => string;
    reset: (widgetId?: number) => void;
  };
}

class RecaptchaService {
  private v3Loaded = false;
  private v2Loaded = false;
  private v3LoadPromise: Promise<void> | null = null;
  private v2LoadPromise: Promise<void> | null = null;
  private v2WidgetId: number | null = null;
  private badgeHidden = false;

  /**
   * Masque le badge reCAPTCHA
   */
  hideBadge(): void {
    if (typeof window === 'undefined') return;
    
    // Masquer le badge reCAPTCHA avec CSS
    const style = document.createElement('style');
    style.id = 'recaptcha-badge-hide';
    style.innerHTML = `
      .grecaptcha-badge {
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    
    // Supprimer l'ancien style s'il existe
    const existingStyle = document.getElementById('recaptcha-badge-hide');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    document.head.appendChild(style);
    this.badgeHidden = true;
  }

  /**
   * Restaure l'affichage du badge reCAPTCHA
   */
  showBadge(): void {
    if (typeof window === 'undefined') return;
    
    // Supprimer le style qui masque le badge
    const style = document.getElementById('recaptcha-badge-hide');
    if (style) {
      style.remove();
    }
    
    this.badgeHidden = false;
  }

  /**
   * Vérifie si le badge est masqué
   */
  isBadgeHidden(): boolean {
    return this.badgeHidden;
  }

  /**
   * Charge le script reCAPTCHA v3
   */
  private loadV3Script(): Promise<void> {
    if (this.v3Loaded) {
      return Promise.resolve();
    }

    if (this.v3LoadPromise) {
      return this.v3LoadPromise;
    }

    this.v3LoadPromise = new Promise((resolve, reject) => {
      // Vérifier si le script est déjà chargé
      if (typeof window !== 'undefined' && (window as GrecaptchaWindow).grecaptcha) {
        this.v3Loaded = true;
        resolve();
        return;
      }

      // Créer le script
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_V3_SITE_KEY}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.v3Loaded = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load reCAPTCHA v3 script'));
      };

      document.head.appendChild(script);
    });

    return this.v3LoadPromise;
  }

  /**
   * Charge le script reCAPTCHA v2
   */
  private loadV2Script(): Promise<void> {
    if (this.v2Loaded) {
      return Promise.resolve();
    }

    if (this.v2LoadPromise) {
      return this.v2LoadPromise;
    }

    this.v2LoadPromise = new Promise((resolve, reject) => {
      // Vérifier si le script est déjà chargé
      if (typeof window !== 'undefined' && (window as GrecaptchaWindow).grecaptcha) {
        this.v2Loaded = true;
        resolve();
        return;
      }

      // Créer le script
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.v2Loaded = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load reCAPTCHA v2 script'));
      };

      document.head.appendChild(script);
    });

    return this.v2LoadPromise;
  }

  /**
   * Génère un token reCAPTCHA v3
   */
  async generateV3Token(action: string = 'chat_message'): Promise<string> {
    try {
      await this.loadV3Script();

      return new Promise((resolve, reject) => {
        if (typeof window === 'undefined' || !(window as GrecaptchaWindow).grecaptcha) {
          reject(new Error('reCAPTCHA not available'));
          return;
        }

        (window as GrecaptchaWindow).grecaptcha!.ready(() => {
          (window as GrecaptchaWindow).grecaptcha!.execute(RECAPTCHA_V3_SITE_KEY, { action })
            .then((token: string) => {
              resolve(token);
            })
            .catch((error: Error) => {
              reject(new Error(`reCAPTCHA v3 execution failed: ${error.message}`));
            });
        });
      });
    } catch (error) {
      logger.error('Error generating reCAPTCHA v3 token', error, 'CaptchaService');
      throw error;
    }
  }

  /**
   * Génère un token reCAPTCHA v2 (nécessite interaction utilisateur)
   */
  async generateV2Token(containerId: string = 'recaptcha-v2-container', targetElement?: HTMLElement): Promise<string> {
    try {
      await this.loadV2Script();

      return new Promise((resolve, reject) => {
        if (typeof window === 'undefined' || !(window as GrecaptchaWindow).grecaptcha) {
          reject(new Error('reCAPTCHA not available'));
          return;
        }

        (window as GrecaptchaWindow).grecaptcha!.ready(() => {
          // Créer le conteneur si nécessaire
          let container = document.getElementById(containerId);
          if (!container) {
            container = document.createElement('div');
            container.id = containerId;
            
            // Positionnement intelligent : sous le champ de saisie ou modal centrée
            if (targetElement) {
              // Positionner sous l'élément cible (champ de saisie)
              const rect = targetElement.getBoundingClientRect();
              container.style.position = 'fixed';
              container.style.top = `${rect.bottom + 10}px`; // 10px sous l'élément
              container.style.left = `${rect.left}px`;
              container.style.zIndex = '9999';
              container.style.backgroundColor = 'white';
              container.style.padding = '15px';
              container.style.borderRadius = '8px';
              container.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              container.style.border = '1px solid #e5e7eb';
              container.style.minWidth = '300px';
            } else {
              // Fallback : modal centrée
              container.style.position = 'fixed';
              container.style.top = '50%';
              container.style.left = '50%';
              container.style.transform = 'translate(-50%, -50%)';
              container.style.zIndex = '9999';
              container.style.backgroundColor = 'white';
              container.style.padding = '20px';
              container.style.borderRadius = '8px';
              container.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }
            
            document.body.appendChild(container);
          }

          // Rendre le widget reCAPTCHA v2
          this.v2WidgetId = (window as GrecaptchaWindow).grecaptcha!.render(container, {
            sitekey: RECAPTCHA_V2_SITE_KEY,
            callback: (token: string) => {
              // Nettoyer le conteneur
              if (container && container.parentNode) {
                container.parentNode.removeChild(container);
              }
              resolve(token);
            },
            'expired-callback': () => {
              if (container && container.parentNode) {
                container.parentNode.removeChild(container);
              }
              reject(new Error('reCAPTCHA v2 expired'));
            },
            'error-callback': () => {
              if (container && container.parentNode) {
                container.parentNode.removeChild(container);
              }
              reject(new Error('reCAPTCHA v2 error'));
            }
          });
        });
      });
    } catch (error) {
      logger.error('Error generating reCAPTCHA v2 token', error, 'CaptchaService');
      throw error;
    }
  }

  /**
   * Génère un token avec fallback automatique v3 → v2
   */
  async generateTokenWithFallback(action: string = 'chat_message'): Promise<{ token: string; version: 'v3' | 'v2' }> {
    try {
      // Essayer d'abord v3
      const v3Token = await this.generateV3Token(action);
      return { token: v3Token, version: 'v3' };
    } catch (error) {
      logger.info('reCAPTCHA v3 failed, falling back to v2', error, 'CaptchaService');
      
      // Fallback vers v2
      const v2Token = await this.generateV2Token();
      return { token: v2Token, version: 'v2' };
    }
  }

  /**
   * Vérifie si reCAPTCHA est disponible
   */
  isAvailable(): boolean {
    return typeof window !== 'undefined' && !!(window as GrecaptchaWindow).grecaptcha;
  }

  /**
   * Génère un token reCAPTCHA v3 (méthode legacy pour compatibilité)
   */
  async generateToken(action: string = 'chat_message'): Promise<string> {
    const result = await this.generateTokenWithFallback(action);
    return result.token;
  }
}

// Instance singleton
export const recaptchaService = new RecaptchaService();

// Hook pour utiliser reCAPTCHA dans les composants React
export const useRecaptcha = () => {
  const generateToken = async (action: string = 'chat_message'): Promise<string> => {
    return recaptchaService.generateToken(action);
  };

  const generateTokenWithFallback = async (action: string = 'chat_message'): Promise<{ token: string; version: 'v3' | 'v2' }> => {
    return recaptchaService.generateTokenWithFallback(action);
  };

  const generateV2Token = async (containerId?: string): Promise<string> => {
    return recaptchaService.generateV2Token(containerId);
  };

  const isAvailable = (): boolean => {
    return recaptchaService.isAvailable();
  };

  return {
    generateToken,
    generateTokenWithFallback,
    generateV2Token,
    isAvailable,
  };
}; 